import { TableConfigV2 } from 'hooks/useMuiTableBuilderV2';
/* --------------------------------- Header --------------------------------- */
import ReplicaDatabaseToAgentHeaderCell from './Cells/HeaderCells/ReplicaDatabaseToAgentHeaderCell';
import AgentToIntegrationServerHeaderCell from './Cells/HeaderCells/AgentToIntegrationServerHeaderCell';
import DataTypeHeaderCell from './Cells/HeaderCells/DataTypeHeaderCell';
import DaysHeaderCell from './Cells/HeaderCells/DaysHeaderCell';
import ExceededRetriesHeaderCell from './Cells/HeaderCells/ExceededRetriesHeaderCell';
import IntegrationServerToAgentHeaderCell from './Cells/HeaderCells/IntegrationServerToAgentHeaderCell';
import IntegrationServerToIntegrationHeaderCell from './Cells/HeaderCells/IntegrationServerToIntegrationHeaderCell';
import TotalAvailableForImportsHeaderCell from './Cells/HeaderCells/TotalAvailableForImportsHeaderCell';
import IntegrationServerToMosaicHeaderCell from './Cells/HeaderCells/IntegrationServerToMosaicHeaderCell';
import TotalPendingHeaderCell from './Cells/HeaderCells/TotalPendingHeaderCell';
import SyncedHeaderCell from './Cells/HeaderCells/SyncedHeaderCell';
import TotalOnIntegrationServerHeaderCell from './Cells/HeaderCells/TotalOnIntegrationServerHeaderCell';
import TotalOnAgentHeaderCell from './Cells/HeaderCells/TotalOnAgentHeaderCell';
import TotalOnReplicaDatabaseHeaderCell from './Cells/HeaderCells/TotalOnReplicaDatabaseHeaderCell';
import TotalPersistingHeaderCell from './Cells/HeaderCells/TotalPersistingHeaderCell';
/* --------------------------------- Section Header -------------------------------- */
import DetailsSectionCell from './Cells/SectionHeaderCells/DetailsSectionCell';
import PendingSectionCell from './Cells/SectionHeaderCells/PendingSectionCell';
import TotalsSectionCell from './Cells/SectionHeaderCells/TotalsSectionCell';
import TotalPersistingSectionCell from './Cells/SectionHeaderCells/TotalPersistingSectionCell';
/* --------------------------------- Counts --------------------------------- */
import TotalAvailableForImportsCountsCell from './Cells/CountsCells/TotalAvailableForImportsCountsCell';
import ReplicaDatabaseToAgentCountsCell from './Cells/CountsCells/ReplicaDatabaseToAgentCountsCell';
import AgentToIntegrationServerCountsCell from './Cells/CountsCells/AgentToIntegrationServerCountsCell';
import DataTypeCountsCell from './Cells/CountsCells/DataTypeCountsCell';
import DaysCountsCell from './Cells/CountsCells/DaysCountsCell';
import ExceededRetriesCountsCell from './Cells/CountsCells/ExceededRetriesCountsCell';
import IntegrationServerToAgentCountsCell from './Cells/CountsCells/IntegrationServerToAgentCountsCell';
import IntegrationServerToIntegrationCountsCell from './Cells/CountsCells/IntegrationServerToIntegrationCountsCell';
import IntegrationServerToMosaicCountsCell from './Cells/CountsCells/IntegrationServerToMosaicCountsCell';
import TotalPendingCountsCell from './Cells/CountsCells/TotalPendingCountsCell';
import SyncedCountsCell from './Cells/CountsCells/SyncedCountsCell';
import TotalOnIntegrationServerCountsCell from './Cells/CountsCells/TotalOnIntegrationServerCountsCell';
import TotalOnAgentCountsCell from './Cells/CountsCells/TotalOnAgentCountsCell';
import TotalOnReplicaDatabaseCountsCell from './Cells/CountsCells/TotalOnReplicaDatabaseCountsCell';
import TotalPersistingCountsCell from './Cells/CountsCells/TotalPersistingCountsCell';
import OptionCell from '../MappingStatsPerDataTypes/Cells/CountsCells/OptionCell';
import omit from 'lodash/omit';

const EmptyDiv = () => <div />;

export const entityMappingStateId = 'fetch-entity-mapping-state-id';
export const dataCountByEntityStateId = 'mapping-status-per-data-type-table';

export const defaultDay = 7;

export enum TableVariants {
  FullTable = 'FullTable',
  WithoutDetailsGroup = 'WithoutDetailsGroup',
  /* -------------------------------------------------------------------------- */
  TwoWaySyncFullTable = 'TwoWaySyncFullTable', // Has pending to target
  TwoWaySyncWithoutDetailsGroup = 'TwoWaySyncWithoutDetailsGroup',

  /* --------------------------- Replication Integrations -------------------------- */
  // These integrations don't have  "Total on Replica Database" column. See REPLICATION_INTEGRATIONS
  ReplicationIntegrationsTable = 'ReplicationIntegrationsTable',
  ReplicationIntegrationsWithoutDetailsGroup = 'ReplicationIntegrationsWithoutDetailsGroup',
}

export const COLUMNS = {
  DATA_TYPE: 'DATA_TYPE',
  OPTION: 'OPTION',
  /* --------------------------------- Pending -------------------------------- */
  IS_TO_MOSAIC: 'IS_TO_MOSAIC',
  IS_TO_INTEGRATION: 'IS_TO_INTEGRATION',
  IS_TO_AGENT: 'IS_TO_AGENT', // IS_TO_INTEGRATION = IS_TO_AGENT, but IS_TO_INTEGRATION is more precise
  // AGENT_TO_IS: "AGENT_TO_IS", No Such counts according to IS devs
  REPLICA_DATABASE_TO_AGENT: 'REPLICA_DATABASE_TO_AGENT',
  /* --------------------------------- Totals --------------------------------- */
  SYNCED: 'SYNCED',
  TOTAL_ON_AGENT: 'TOTAL_ON_AGENT',
  TOTAL_ON_REPLICA_DATABASE: 'TOTAL_ON_REPLICA_DATABASE',
  TOTAL_PENDING: 'TOTAL_PENDING',
  TOTAL_AVAILABLE_FOR_IMPORTS: 'TOTAL_AVAILABLE_FOR_IMPORTS',

  /* --------------------------------- Details -------------------------------- */
  EXCEEDED_RETRIES_TO_MOSAIC: 'EXCEEDED_RETRIES_TO_MOSAIC',
  LAST_DAYS_TO_MOSAIC: 'LAST_DAYS_TO_MOSAIC',
  TOTAL_PERSISTING_TO_MOSAIC: 'TOTAL_PERSISTING_TO_MOSAIC',
};

export const ROW_TYPES = {
  SECTION_HEADERS: 'SECTION_HEADERS',
  HEADERS: 'HEADERS',
  DATA: 'DATA',
};

const tableConfigs = {
  /* ------------------------------ Two ways sync ----------------------------- */
  // integration/details/team/3375/targetService/quickbooks-desktop/targetServiceId/206
  [TableVariants.TwoWaySyncFullTable]: {
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
      //
      COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC,
      COLUMNS.LAST_DAYS_TO_MOSAIC,
      COLUMNS.TOTAL_PERSISTING_TO_MOSAIC,
    ],
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_AGENT]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DetailsSectionCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingSectionCell,
      },
    },
  },

  [TableVariants.TwoWaySyncWithoutDetailsGroup]: {
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
    ],
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_AGENT]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
      },
    },
  },

  /* ------------------------------ Replication integration (1 way sync) ----------------------------- */
  // http://localhost:3001/integration/details/team/5026/targetService/deltek-ajera-hosted/targetServiceId/231
  [TableVariants.ReplicationIntegrationsTable]: {
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
      COLUMNS.TOTAL_ON_REPLICA_DATABASE,
      //
      COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC,
      COLUMNS.LAST_DAYS_TO_MOSAIC,
      COLUMNS.TOTAL_PERSISTING_TO_MOSAIC,
    ],
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: EmptyDiv,
        [COLUMNS.TOTAL_ON_AGENT]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DetailsSectionCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingSectionCell,
      },
    },
  },
  [TableVariants.ReplicationIntegrationsWithoutDetailsGroup]: {
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: EmptyDiv,
        [COLUMNS.TOTAL_ON_AGENT]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: EmptyDiv,
      },
    },
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
      COLUMNS.TOTAL_ON_REPLICA_DATABASE,
    ],
  },

  /* ------------------------------ Other One way sync integration ----------------------------- */
  // http://localhost:3001/integration/details/team/4045/targetService/google-calendar/targetServiceId/7
  // One way sync
  [TableVariants.FullTable]: {
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
      //
      COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC,
      COLUMNS.LAST_DAYS_TO_MOSAIC,
      COLUMNS.TOTAL_PERSISTING_TO_MOSAIC,
    ],
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_AGENT]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DetailsSectionCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingSectionCell,
      },
    },
  },

  // One way without details sync
  [TableVariants.WithoutDetailsGroup]: {
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: PendingSectionCell,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_AGENT]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
      },
    },
    columns: [
      COLUMNS.OPTION,
      COLUMNS.DATA_TYPE,
      COLUMNS.IS_TO_MOSAIC,
      COLUMNS.IS_TO_INTEGRATION,
      COLUMNS.TOTAL_PENDING,
      //
      COLUMNS.SYNCED,
      COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS,
      COLUMNS.TOTAL_ON_AGENT,
    ],
  },
};

export const getTableConfig = ({
  variant,
}: {
  variant: keyof typeof TableVariants;
}): TableConfigV2 => {
  const variantConfig = tableConfigs[variant];
  const customConfigToUse = omit(variantConfig, 'cellsByRowTypesAndColumns');
  const config = {
    /* --------------------------------- Shared --------------------------------- */
    hasStickyHeader: true,
    tableHeaderTitle: 'Data Type Integration Statuses',
    rowTypes: ROW_TYPES,
    columnsMinWidth: {
      [COLUMNS.OPTION]: 30,
    },
    cellsByRowTypesAndColumns: {
      [ROW_TYPES.DATA]: {
        [COLUMNS.OPTION]: OptionCell,
        [COLUMNS.DATA_TYPE]: DataTypeCountsCell,
        [COLUMNS.IS_TO_MOSAIC]: IntegrationServerToMosaicCountsCell,
        [COLUMNS.IS_TO_INTEGRATION]: IntegrationServerToIntegrationCountsCell,
        [COLUMNS.IS_TO_AGENT]: IntegrationServerToAgentCountsCell,
        // [COLUMNS.AGENT_TO_IS]: AgentToIntegrationServerCountsCell,
        [COLUMNS.REPLICA_DATABASE_TO_AGENT]: ReplicaDatabaseToAgentCountsCell,
        [COLUMNS.SYNCED]: SyncedCountsCell,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalAvailableForImportsCountsCell,
        [COLUMNS.TOTAL_ON_AGENT]: TotalOnAgentCountsCell,
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: TotalOnReplicaDatabaseCountsCell,
        [COLUMNS.TOTAL_PENDING]: TotalPendingCountsCell,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: ExceededRetriesCountsCell,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DaysCountsCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingCountsCell,
      },
      [ROW_TYPES.SECTION_HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: EmptyDiv,
        [COLUMNS.IS_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.TOTAL_PENDING]: EmptyDiv,
        [COLUMNS.IS_TO_INTEGRATION]: EmptyDiv,
        [COLUMNS.IS_TO_AGENT]: EmptyDiv,
        // [COLUMNS.AGENT_TO_IS]: PendingSectionCell,
        [COLUMNS.REPLICA_DATABASE_TO_AGENT]: EmptyDiv,
        [COLUMNS.SYNCED]: EmptyDiv,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: EmptyDiv,
        [COLUMNS.TOTAL_ON_AGENT]: TotalsSectionCell,
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: EmptyDiv,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: EmptyDiv,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DetailsSectionCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingSectionCell,
      },
      [ROW_TYPES.HEADERS]: {
        [COLUMNS.OPTION]: EmptyDiv,
        [COLUMNS.DATA_TYPE]: DataTypeHeaderCell,
        [COLUMNS.IS_TO_MOSAIC]: IntegrationServerToMosaicHeaderCell,
        [COLUMNS.IS_TO_INTEGRATION]: IntegrationServerToIntegrationHeaderCell,
        [COLUMNS.IS_TO_AGENT]: IntegrationServerToAgentHeaderCell,
        // [COLUMNS.AGENT_TO_IS]: AgentToIntegrationServerHeaderCell,
        [COLUMNS.REPLICA_DATABASE_TO_AGENT]: ReplicaDatabaseToAgentHeaderCell,
        [COLUMNS.SYNCED]: SyncedHeaderCell,
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: TotalAvailableForImportsHeaderCell,
        [COLUMNS.TOTAL_ON_AGENT]: TotalOnAgentHeaderCell,
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: TotalOnReplicaDatabaseHeaderCell,
        [COLUMNS.TOTAL_PENDING]: TotalPendingHeaderCell,
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: ExceededRetriesHeaderCell,
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: DaysHeaderCell,
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: TotalPersistingHeaderCell,
      },
      ...(variantConfig as any).cellsByRowTypesAndColumns,
    },
    /* -------------------------------- Variant  ------------------------------- */
    ...customConfigToUse,
  };

  return config;
};
